<script lang="ts">
  import DialogWrapper from '../ui/dialog-wrapper.svelte';
  import Card from '../ui/card.svelte';
  import { _ } from 'svelte-i18n';

  let {
    open = true,
    onClose
  }: {
    open?: boolean;
    onClose: () => void;
  } = $props();
</script>

<DialogWrapper {open} {onClose} title={$_('test.info_dialog.title')}>
  <div class="space-y-6">
    <div class="space-y-4 text-foreground/80">
      <!-- Exercise Modes -->
      <Card class="p-5">
        <h3 class="mb-3 flex items-center gap-2 text-lg font-semibold text-foreground">
          <span>🎯</span> {$_('test.info_dialog.exercise_modes.title')}
        </h3>
        <ul class="space-y-2 text-sm">
          <li class="flex items-start gap-2">
            <span class="text-purple-500">•</span>
            <span><strong>{$_('test.info_dialog.exercise_modes.type_in')}</strong> - {$_('test.info_dialog.exercise_modes.type_in_desc')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-purple-500">•</span>
            <span><strong>{$_('test.info_dialog.exercise_modes.multiple_choice')}</strong> - {$_('test.info_dialog.exercise_modes.multiple_choice_desc')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-purple-500">•</span>
            <span><strong>{$_('test.info_dialog.exercise_modes.listen_type')}</strong> - {$_('test.info_dialog.exercise_modes.listen_type_desc')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-purple-500">•</span>
            <span><strong>{$_('test.info_dialog.exercise_modes.match_pairs')}</strong> - {$_('test.info_dialog.exercise_modes.match_pairs_desc')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-purple-500">•</span>
            <span><strong>{$_('test.info_dialog.exercise_modes.cloze')}</strong> - {$_('test.info_dialog.exercise_modes.cloze_desc')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-purple-500">•</span>
            <span><strong>{$_('test.info_dialog.exercise_modes.word_build')}</strong> - {$_('test.info_dialog.exercise_modes.word_build_desc')}</span>
          </li>
        </ul>
      </Card>

      <!-- Test Types -->
      <Card class="p-5">
        <h3 class="mb-3 flex items-center gap-2 text-lg font-semibold text-foreground">
          <span>📝</span> {$_('test.info_dialog.test_types.title')}
        </h3>
        <div class="space-y-3">
          <div class="rounded-lg bg-accent/10 p-3">
            <div class="mb-1 flex items-center gap-2">
              <span class="text-xl">🧠</span>
              <strong class="text-foreground">{$_('test.info_dialog.test_types.smart.title')}</strong>
            </div>
            <p class="text-sm">{$_('test.info_dialog.test_types.smart.desc')}</p>
          </div>

          <div class="rounded-lg bg-secondary/10 p-3">
            <div class="mb-1 flex items-center gap-2">
              <span class="text-xl">⚙️</span>
              <strong class="text-foreground">{$_('test.info_dialog.test_types.custom.title')}</strong>
            </div>
            <p class="text-sm">{$_('test.info_dialog.test_types.custom.desc')}</p>
          </div>
        </div>
      </Card>

      <!-- FSRS Algorithm -->
      <Card class="p-5">
        <h3 class="mb-3 flex items-center gap-2 text-lg font-semibold text-foreground">
          <span>🧠</span> {$_('test.info_dialog.fsrs.title')}
        </h3>
        <div class="space-y-3 text-sm">
          <p>{$_('test.info_dialog.fsrs.description')}</p>

          <div class="rounded-lg bg-gradient-to-r from-purple-500/10 to-blue-500/10 p-3">
            <p class="mb-2 font-semibold text-foreground">{$_('test.info_dialog.fsrs.how_it_works')}</p>
            <ul class="space-y-1.5">
              <li class="flex items-start gap-2">
                <span class="text-purple-500">•</span>
                <span>{$_('test.info_dialog.fsrs.card_selection')}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-purple-500">•</span>
                <span>{$_('test.info_dialog.fsrs.rating_system')}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-purple-500">•</span>
                <span>{$_('test.info_dialog.fsrs.scheduling')}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-purple-500">•</span>
                <span>{$_('test.info_dialog.fsrs.optimization')}</span>
              </li>
            </ul>
          </div>

          <div>
            <p class="mb-2 font-semibold text-foreground">{$_('test.info_dialog.fsrs.rating_explanation')}</p>
            <ul class="space-y-1.5">
              <li class="flex items-start gap-2">
                <span class="text-red-500">•</span>
                <span><strong>Again (1):</strong> {$_('test.info_dialog.fsrs.rating_again')}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-orange-500">•</span>
                <span><strong>Hard (2):</strong> {$_('test.info_dialog.fsrs.rating_hard')}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-green-500">•</span>
                <span><strong>Good (3):</strong> {$_('test.info_dialog.fsrs.rating_good')}</span>
              </li>
              <li class="flex items-start gap-2">
                <span class="text-blue-500">•</span>
                <span><strong>Easy (4):</strong> {$_('test.info_dialog.fsrs.rating_easy')}</span>
              </li>
            </ul>
          </div>
        </div>
      </Card>

      <!-- Tips -->
      <Card class="p-5">
        <h3 class="mb-3 flex items-center gap-2 text-lg font-semibold text-foreground">
          <span>💡</span> {$_('test.info_dialog.tips.title')}
        </h3>
        <ul class="space-y-2 text-sm">
          <li class="flex items-start gap-2">
            <span class="text-primary">•</span>
            <span>{$_('test.info_dialog.tips.tip1')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-primary">•</span>
            <span>{$_('test.info_dialog.tips.tip2')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-primary">•</span>
            <span>{$_('test.info_dialog.tips.tip3')}</span>
          </li>
          <li class="flex items-start gap-2">
            <span class="text-primary">•</span>
            <span>{$_('test.info_dialog.tips.tip4')}</span>
          </li>
        </ul>
      </Card>
    </div>
  </div>
</DialogWrapper>
